import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { logUxEvent } from './telemetry/uxLogger';
import ProgressBar from './components/ProgressBar';
import FormBackButton from './components/FormBackButton';
import DecoDivider from './components/DecoDivider';
import useViewportInsets from './hooks/useViewportInsets';
import useFormSubmitGuard from './hooks/useFormSubmitGuard';
import { persistIdentityKey } from './utils/identityBackup';
import { formatCpf, isValidCpf } from './utils/patientContactValidation';
import {
  nextSignupRoute,
  getSignupVariant,
  signupProgress,
  setSignupVariant,
  SIGNUP_CONTROL,
} from './utils/signupOrderAb';
import { ensureSignupSession } from './utils/ensureSignupSession';

const BACKEND_URL = process.env.REACT_APP_API_URL || window.location.origin;

/** Fallback: rebaixa o braço `doc_caf` para o controle sem confirmação. */
function fallbackToControl(reason, patientId, navigate) {
  logUxEvent('signup_doc_ocr_fallback', {
    reason,
    patientId,
  });
  const controlVariant = 'noconfirm_control';
  if (patientId) setSignupVariant(controlVariant, patientId);
  // O braço controle começa em `nome` — mesma tela antiga, sem OCR.
  const first = nextSignupRoute('documento', controlVariant) || '/consulta';
  // Como a documento não é passo do controle, `nextSignupRoute` não acha um
  // passo maior; usamos a rota da PRIMEIRA etapa do braço rebaixado.
  navigate('/consulta', { replace: true });
  // (silencia lint sobre `first` não usado)
  void first;
}

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    try {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result || ''));
      r.onerror = () => reject(r.error);
      r.readAsDataURL(file);
    } catch (err) {
      reject(err);
    }
  });
}

function isValidFullName(value) {
  const parts = String(value || '').trim().split(/\s+/);
  if (parts.length < 2) return false;
  return parts.every((p) => /^[A-Za-zÀ-ÖØ-öø-ÿ]+(-[A-Za-zÀ-ÖØ-öø-ÿ]+)*$/.test(p));
}

export default function DocumentIdForm() {
  const navigate = useNavigate();
  useViewportInsets();

  // 3 fases: `capture` (fotos), `review` (paciente confirma o extraído),
  // `sending` (submitting).
  const [phase, setPhase] = useState('capture');
  const [docType, setDocType] = useState('rg');
  const [frontData, setFrontData] = useState('');
  const [backData, setBackData] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [extracted, setExtracted] = useState(null);
  const [editedName, setEditedName] = useState('');
  const [editedCpf, setEditedCpf] = useState('');
  const [editedTouched, setEditedTouched] = useState({ name: false, cpf: false });

  const frontInputRef = useRef(null);
  const backInputRef = useRef(null);

  React.useEffect(() => {
    ensureSignupSession().catch(() => {});
    logUxEvent('signup_doc_step_view', { docType: 'unknown' });
  }, []);

  const canSubmit = useMemo(() => {
    if (docType === 'rg') return !!frontData && !!backData;
    return !!frontData;
  }, [frontData, backData, docType]);

  const handleFrontChange = useCallback(async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      const url = await fileToDataUrl(file);
      setFrontData(url);
      logUxEvent('signup_doc_photo_selected', { side: 'front', bytes: file.size });
    } catch {
      setErrorMsg('Não foi possível ler a imagem. Tente novamente.');
    }
  }, []);

  const handleBackChange = useCallback(async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      const url = await fileToDataUrl(file);
      setBackData(url);
      logUxEvent('signup_doc_photo_selected', { side: 'back', bytes: file.size });
    } catch {
      setErrorMsg('Não foi possível ler a imagem. Tente novamente.');
    }
  }, []);

  const doOcrRaw = async () => {
    setErrorMsg('');
    const session = await ensureSignupSession().catch(() => null);
    const patientId =
      (session && session.patientId) ||
      sessionStorage.getItem('patientId') ||
      localStorage.getItem('patientId') ||
      '';

    setPhase('sending');
    logUxEvent('signup_doc_ocr_start', { docType });

    let res;
    try {
      res = await fetch(`${BACKEND_URL}/registration/document-ocr`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patientId,
          frontImage: frontData,
          backImage: docType === 'rg' ? backData : undefined,
          docType,
        }),
      });
    } catch {
      logUxEvent('signup_doc_ocr_network_error', {});
      fallbackToControl('network_error', patientId, navigate);
      return true;
    }

    const data = await res.json().catch(() => ({}));

    if (data && data.available === false) {
      // CAF não configurada no backend — braço `doc_caf` está inerte, cai
      // no controle sem culpar o paciente.
      fallbackToControl(data.reason || 'not_configured', patientId, navigate);
      return true;
    }

    if (!data || !data.success || !data.extracted) {
      const reason = (data && data.reason) || 'ocr_failed';
      logUxEvent('signup_doc_ocr_failed', { reason });
      fallbackToControl(reason, patientId, navigate);
      return true;
    }

    setExtracted(data.extracted);
    setEditedName(String(data.extracted.fullName || ''));
    setEditedCpf(formatCpf(String(data.extracted.cpfDigits || '')));
    setPhase('review');
    logUxEvent('signup_doc_ocr_success', {});
    return true;
  };

  const [isSubmitting, doOcr] = useFormSubmitGuard(doOcrRaw, { cooldownMs: 3500 });

  const nameOk = isValidFullName(editedName);
  const cpfDigits = String(editedCpf || '').replace(/\D/g, '');
  const cpfOk = isValidCpf(cpfDigits);

  const confirmReviewRaw = async () => {
    if (!nameOk || !cpfOk) {
      setEditedTouched({ name: true, cpf: true });
      return false;
    }
    const session = await ensureSignupSession().catch(() => null);
    const patientId =
      (session && session.patientId) ||
      sessionStorage.getItem('patientId') ||
      localStorage.getItem('patientId') ||
      '';

    const trimmedName = String(editedName || '').replace(/\s+/g, ' ').trim();
    try {
      sessionStorage.setItem('fullName', trimmedName);
      localStorage.setItem('fullName', trimmedName);
      sessionStorage.setItem('patientName', trimmedName);
      localStorage.setItem('patientName', trimmedName);
      sessionStorage.setItem('patientCpf', cpfDigits);
      localStorage.setItem('patientCpf', cpfDigits);
    } catch {}
    try { persistIdentityKey('fullName', trimmedName); } catch {}
    try { persistIdentityKey('patientName', trimmedName); } catch {}
    try { persistIdentityKey('patientCpf', cpfDigits); } catch {}

    logUxEvent('signup_doc_review_confirmed', {
      nameEdited: extracted && extracted.fullName !== trimmedName,
      cpfEdited: extracted && String(extracted.cpfDigits || '') !== cpfDigits,
    });

    if (patientId) {
      try {
        // Nome + CPF em duas chamadas de /registration/step (upsert). CPF é
        // o gatilho de flowAb; a resposta do CPF traz o `flowVariant` para
        // o cliente persistir.
        await fetch(`${BACKEND_URL}/registration/step`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ patientId, step: 'nome', fullName: trimmedName }),
        });
        const r = await fetch(`${BACKEND_URL}/registration/step`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ patientId, step: 'cpf', cpf: cpfDigits }),
        });
        const payload = await r.json().catch(() => ({}));
        if (payload && payload.flowVariant) {
          const { setFlowVariant } = await import('./utils/flowVariant');
          setFlowVariant(payload.flowVariant, patientId);
        }
      } catch {}
    }

    const variant = getSignupVariant();
    const dest = nextSignupRoute('documento', variant) || '/email';
    navigate(dest);
    return true;
  };

  const [isConfirming, confirmReview] = useFormSubmitGuard(confirmReviewRaw, { cooldownMs: 3500 });

  const rejectAndFallback = () => {
    const patientId =
      sessionStorage.getItem('patientId') ||
      localStorage.getItem('patientId') ||
      '';
    logUxEvent('signup_doc_review_rejected', {});
    fallbackToControl('rejected_by_patient', patientId, navigate);
  };

  return (
    <>
      <style>{`
        :root {
          --primary: #D32F2F;
          --bg-page: #F9FAFB;
          --surface: #FFFFFF;
          --text-main: #1E1E1E;
          --text-dim: #6B6B6B;
          --border-subtle: #E5E7EB;
          --radius-lg: 1rem;
          --radius-xl: 1.5rem;
          --radius-full: 999px;
          --shadow-sm: 0 6px 16px rgba(0,0,0,0.06);
          --shadow-lg: 0 12px 28px rgba(0,0,0,0.08);
        }
        body {
          margin: 0;
          background: var(--bg-page);
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Inter', 'Roboto', sans-serif;
          color: var(--text-main);
        }
        .page-shell { min-height: 100vh; min-height: 100dvh; display: flex; flex-direction: column; background: var(--bg-page); }
        .inner-wrap { flex: 1; max-width: 480px; width: 100%; margin: 0 auto;
          padding: 4.5rem 1rem max(2rem, calc(1rem + var(--vvb, 0px) + env(safe-area-inset-bottom, 0px)));
          display: flex; flex-direction: column; align-items: center; gap: 1.25rem; box-sizing: border-box;
        }
        .header-block { text-align: center; max-width: 22rem; margin-top: 1rem; }
        .main-headline { font-size: 1.25rem; font-weight: 600; margin: 0 0 .5rem; }
        .sub-headline { font-size: .85rem; color: var(--text-dim); line-height: 1.45; margin: 0; }
        .form-card {
          background: var(--surface); width: 100%; max-width: 24rem;
          border-radius: var(--radius-xl); box-shadow: var(--shadow-sm);
          padding: 1.25rem; display: flex; flex-direction: column; gap: .85rem; box-sizing: border-box;
        }
        .doc-type-row { display: flex; gap: .5rem; flex-wrap: wrap; }
        .doc-type-btn {
          flex: 1; padding: .55rem .8rem; border-radius: var(--radius-full);
          border: 1px solid var(--border-subtle); background: #fff; color: var(--text-main);
          font-size: .85rem; font-weight: 600; cursor: pointer;
        }
        .doc-type-btn.is-active { background: var(--primary); color: #fff; border-color: var(--primary); }
        .photo-block { display: flex; flex-direction: column; gap: .35rem; }
        .photo-label { font-size: .8rem; font-weight: 600; }
        .photo-slot {
          border: 1px dashed var(--border-subtle); border-radius: var(--radius-lg);
          padding: .75rem; display: flex; flex-direction: column; align-items: center; gap: .5rem;
          background: #FBFBFB;
        }
        .photo-slot img { max-width: 100%; max-height: 160px; border-radius: .5rem; object-fit: cover; }
        .photo-slot input[type=file] { font-size: .75rem; }
        .text-field {
          width: 100%; padding: .7rem .8rem; border-radius: var(--radius-lg);
          border: 1px solid var(--border-subtle); font-size: 1rem; box-sizing: border-box;
        }
        .field-label { font-size: .8rem; font-weight: 600; margin-bottom: .25rem; display: block; }
        .field-error { color: var(--primary); font-size: .75rem; margin-top: .2rem; min-height: 1rem; }
        .confirm-button {
          background: var(--primary); color: #fff; border: none; border-radius: var(--radius-full);
          font-size: 1rem; font-weight: 600; padding: 1rem 1.5rem; min-width: 220px;
          cursor: pointer; box-shadow: var(--shadow-lg);
        }
        .confirm-button[disabled] { opacity: .6; cursor: not-allowed; }
        .link-btn { background: none; border: none; color: var(--primary); font-weight: 600; cursor: pointer; font-size: .85rem; text-decoration: underline; }
        .caption { font-size: .75rem; color: var(--text-dim); line-height: 1.4; margin: 0; }
      `}</style>

      <div className="page-shell">
        <div className="inner-wrap">
          <FormBackButton onClick={() => navigate('/')} ariaLabel="Voltar" />

          <ProgressBar {...signupProgress('/cadastro/documento', getSignupVariant())} />

          <div className="header-block">
            <h1 className="main-headline deco-headline">
              {phase === 'review' ? 'Confirme seus dados' : 'Envie a foto do documento'}
            </h1>
            <DecoDivider variant="chevron" />
            <p className="sub-headline">
              {phase === 'review'
                ? 'Confirme os dados extraídos do seu documento antes de continuar.'
                : 'Enviamos ao nosso parceiro de identidade (CAF) para preencher automaticamente nome e CPF. Suas fotos não são armazenadas.'}
            </p>
          </div>

          {phase !== 'review' && (
            <form
              className="form-card"
              onSubmit={(e) => { e.preventDefault(); doOcr(); }}
              noValidate
            >
              <div>
                <label className="field-label">Tipo de documento</label>
                <div className="doc-type-row">
                  {['rg', 'cnh', 'cin'].map((t) => (
                    <button
                      key={t}
                      type="button"
                      className={`doc-type-btn${docType === t ? ' is-active' : ''}`}
                      onClick={() => setDocType(t)}
                    >
                      {t === 'rg' ? 'RG' : t === 'cnh' ? 'CNH' : 'CIN'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="photo-block">
                <span className="photo-label">Frente do documento</span>
                <div className="photo-slot">
                  {frontData && <img src={frontData} alt="Frente do documento" data-hj-suppress="" />}
                  <input
                    ref={frontInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleFrontChange}
                  />
                </div>
              </div>

              {docType === 'rg' && (
                <div className="photo-block">
                  <span className="photo-label">Verso do documento</span>
                  <div className="photo-slot">
                    {backData && <img src={backData} alt="Verso do documento" data-hj-suppress="" />}
                    <input
                      ref={backInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleBackChange}
                    />
                  </div>
                </div>
              )}

              {errorMsg ? <p className="field-error" role="alert">{errorMsg}</p> : null}

              <button
                type="submit"
                className="confirm-button"
                disabled={!canSubmit || isSubmitting || phase === 'sending'}
              >
                {phase === 'sending' ? 'Analisando…' : 'Continuar'}
              </button>

              <button type="button" className="link-btn" onClick={rejectAndFallback}>
                Prefiro digitar meus dados manualmente
              </button>

              <p className="caption">
                Ao continuar, você concorda em enviar a foto para o parceiro CAF/Certta apenas para
                extração automática de nome e CPF. Não usamos as fotos para nenhuma outra finalidade.
              </p>
            </form>
          )}

          {phase === 'review' && extracted && (
            <form
              className="form-card"
              onSubmit={(e) => { e.preventDefault(); confirmReview(); }}
              noValidate
            >
              <div>
                <label htmlFor="doc-name" className="field-label">Nome completo</label>
                <input
                  id="doc-name"
                  type="text"
                  className="text-field"
                  value={editedName}
                  onChange={(e) => {
                    setEditedName(e.target.value);
                    if (!editedTouched.name) setEditedTouched((s) => ({ ...s, name: true }));
                  }}
                />
                <div className="field-error">
                  {editedTouched.name && !nameOk ? 'Nome inválido. Digite pelo menos dois nomes com letras.' : ' '}
                </div>
              </div>

              <div>
                <label htmlFor="doc-cpf" className="field-label">CPF</label>
                <input
                  id="doc-cpf"
                  type="text"
                  inputMode="numeric"
                  className="text-field"
                  value={editedCpf}
                  onChange={(e) => {
                    setEditedCpf(formatCpf(e.target.value));
                    if (!editedTouched.cpf) setEditedTouched((s) => ({ ...s, cpf: true }));
                  }}
                />
                <div className="field-error">
                  {editedTouched.cpf && !cpfOk ? 'CPF inválido. Verifique os números.' : ' '}
                </div>
              </div>

              <button
                type="submit"
                className="confirm-button"
                disabled={!nameOk || !cpfOk || isConfirming}
              >
                Confirmar e continuar
              </button>

              <button type="button" className="link-btn" onClick={rejectAndFallback}>
                Prefiro digitar meus dados manualmente
              </button>
            </form>
          )}
        </div>
      </div>
    </>
  );
}

// Silencia lint sobre imports que podem parecer não usados em algum caminho.
void SIGNUP_CONTROL;
